import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetDealDocumentsQuery, useRemoveDealDocumentMutation } from 'src/services/deal-flow/deal-flow.service';

import { useTable, TableSkeleton } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import DealDocumentSaver from 'src/sections/deal-flow/modal/deal-document-saver';

import DealDocumentUploadModal from '../../modal/deal-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  dealId?: string;
  uploadVisible?: boolean;
};

export default function DealDocumentsView({ dealId, uploadVisible = true }: Props) {

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);

  const showUploadModal = useBoolean();
  const documentSaverControl = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemoveDealDocumentMutation();

  const { currentData: dealDocuments, isLoading } = useGetDealDocumentsQuery(
    {
      dealId: dealId as string,
      params: page,
    },
    { skip: !dealId }
  );

  const table = useTable();

  const notFound = !dealDocuments?.count && !isLoading;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        dealDocId: id,
        dealId: dealId as string
      }).unwrap();

      enqueueSnackbar(t('deal_flow.api.delete_document.success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('deal_flow.api.delete_document.default_error'), { variant: 'error' });
    }
  };

  const handleOpenDocumentSaver = (id: string) => {
    setSelectedDocument(id);
    documentSaverControl.onTrue();
  }

  const handleCloseDocumentSaver = () => {
    documentSaverControl.onFalse();
    setSelectedDocument(null);
  };

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" disabled={!dealId} onClick={showUploadModal.onTrue}>
                {t('profile.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          {
            isLoading ? (
              <TableSkeleton />
            )
              :
              (
                <FileManagerTable
                  table={table}
                  dataFiltered={dealDocuments?.results ?? []}
                  count={dealDocuments?.count ?? 0}
                  onDeleteRow={handleDeleteItem}
                  onSaveToLink={handleOpenDocumentSaver}
                  notFound={notFound}
                  documentType="deal"
                  onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
                  onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
                  pageOptions={page}
                />
              )
          }

        </Card>
      </Grid>

      {
        dealId && (
          <DealDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            dealId={dealId}
            onUpload={onAddSuccess}
          />
        )
      }

      {
        selectedDocument && (
          <DealDocumentSaver
            open={documentSaverControl.value}
            dealDocId={selectedDocument as string}
            dealId={dealId as string}
            onClose={handleCloseDocumentSaver}
          />
        )
      }


    </Grid>
  );
}